import React, {
  Children,
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
} from "react";

import {
  IconButton,
  IconButtonProps,
  Menu,
  MenuProps,
  Tooltip,
  TooltipProps,
} from "@mui/material";

const NAME = "IconButtonMenu";

export interface IconButtonMenuProps extends IconButtonProps {
  menu: ReactNode;
  menuProps?: Omit<MenuProps, "children" | "open">;
  tooltip?: ReactNode;
  tooltipProps?: Omit<TooltipProps, "title">;
}

export const IconButtonMenu = forwardRef<
  HTMLButtonElement,
  IconButtonMenuProps
>(
  (
    { onClick, tooltip, tooltipProps, menu, menuProps, ...iconButtonProps },
    buttonRef,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      onClick?.(e);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
      <>
        {tooltip ? (
          <Tooltip title={tooltip} {...tooltipProps}>
            <IconButton
              onClick={handleClick}
              ref={buttonRef}
              {...iconButtonProps}
            />
          </Tooltip>
        ) : (
          <IconButton
            onClick={handleClick}
            ref={buttonRef}
            {...iconButtonProps}
          />
        )}
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={handleClose}
          open={open}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          {...menuProps}
        >
          {(() => {
            const processChildren = (children: ReactNode): ReactNode => {
              return Children.map(children, (child) => {
                if (!isValidElement(child)) return child;

                // Check if child is a Fragment
                if (
                  child.type === React.Fragment ||
                  child.type.toString() === "Symbol(react.fragment)"
                ) {
                  // Recursively process fragment children
                  return cloneElement(child as ReactElement, {
                    children: processChildren(child.props.children),
                  });
                }

                // For regular elements, add onClick handler
                return cloneElement(child as ReactElement, {
                  onClick: (e: MouseEvent<HTMLElement>) => {
                    handleClose();
                    if (child.props.onClick) child.props.onClick(e);
                  },
                });
              });
            };

            return processChildren(menu);
          })()}
        </Menu>
      </>
    );
  },
);
IconButtonMenu.displayName = NAME;
